import { useCallback, useMemo, useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";

import type { FileNote, FormContextData, UUID } from "@thalamos/aspire-common";
import {
  getBaseFormTemplate,
  getDeMinimisErrorTypes,
  getLatestVersion,
  resolveDeMinimisFieldLabels,
} from "@thalamos/aspire-common";

import { api } from "../../../api.js";
import {
  Banner,
  BannerList,
  Button,
  PopupDialog,
  PopupDialogTitle,
  renderErrorToast,
  renderSuccessToast,
  TextField,
} from "~/components/design-system/index.js";
import { SignatureDialog } from "~/components/form/SignatureDialog/SignatureDialog.js";

type ModalStep = "review" | "reject" | "complete-details" | "complete-sign";

/** API functions that can be injected for testing */
export interface DeMinimisResponseModalApi {
  rejectDeMinimis: (
    formId: UUID,
    body: { fileNoteId: number; rejectedReason: string },
  ) => Promise<{ status: number }>;
  completeDeMinimis: (
    formId: UUID,
    body: {
      fileNoteId: number;
      response: string;
      signature: string;
      signatoryName: string;
    },
  ) => Promise<{ status: number }>;
}

/** Default API implementation using the real API */
const defaultApi: DeMinimisResponseModalApi = {
  rejectDeMinimis: (formId, body) => api.forms.rejectDeMinimis(formId, body),
  completeDeMinimis: (formId, body) =>
    api.forms.completeDeMinimis(formId, body),
};

export function DeMinimisResponseModal({
  formId,
  fileNote,
  formContext,
  onClose,
  reloadFormContext,
  userName,
  userDefaultSignature,
  apiOverride,
}: {
  formId: UUID;
  fileNote: FileNote;
  formContext: Omit<FormContextData, "patient">;
  onClose: () => void;
  reloadFormContext: () => void;
  userName?: string;
  userDefaultSignature?: string;
  /** Optional API override for testing */
  apiOverride?: DeMinimisResponseModalApi;
}) {
  const apiClient = apiOverride ?? defaultApi;
  const theme = useTheme();
  const form = formContext.forms.find((f) => f.id === formId);
  const template = form ? getBaseFormTemplate(form) : undefined;
  const formDescription = template
    ? `Form ${template.formName}: ${template.description}`
    : "this form";

  const errorTypeLabel = fileNote.deMinimisErrorType
    ? (getDeMinimisErrorTypes(form?.formTemplate.id ?? "").find(
        (e) => e.value === fileNote.deMinimisErrorType,
      )?.label ?? fileNote.deMinimisErrorType)
    : undefined;

  const resolvedFields = useMemo(() => {
    if (
      !fileNote.deMinimisSelectedFields?.length ||
      fileNote.deMinimisTargetFormPart == null ||
      !template ||
      !form
    ) {
      return [];
    }
    const partTemplate = template.parts[fileNote.deMinimisTargetFormPart];
    const latestVersion = getLatestVersion(form);
    if (!partTemplate || !latestVersion?.data) return [];
    const partDataWrapper = (
      latestVersion.data as { data: Record<string, unknown> }[]
    )?.[fileNote.deMinimisTargetFormPart];
    if (!partDataWrapper?.data) return [];
    return resolveDeMinimisFieldLabels(
      partTemplate,
      partDataWrapper.data,
      fileNote.deMinimisSelectedFields,
    );
  }, [fileNote, template, form]);

  const isPending = fileNote.deMinimisStatus === "requested";
  const isAccepted = fileNote.deMinimisStatus === "accepted";

  const [step, setStep] = useState<ModalStep>("review");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reject state
  const [rejectedReason, setRejectedReason] = useState("");

  // Complete state
  const [response, setResponse] = useState("");
  const [signatoryName, setSignatoryName] = useState(userName ?? "");

  const fileNoteId = Number(fileNote.fileNoteId);

  const handleReject = useCallback(async () => {
    if (!rejectedReason.trim()) return;
    setIsSubmitting(true);
    try {
      const result = await apiClient.rejectDeMinimis(formId, {
        fileNoteId,
        rejectedReason: rejectedReason.trim(),
      });
      if (result.status === 204) {
        renderSuccessToast({
          message: "De minimis correction request rejected",
        });
        reloadFormContext();
        onClose();
      }
    } catch (e: unknown) {
      renderErrorToast({
        message: e instanceof Error ? e.message : "An error occurred",
      });
      setIsSubmitting(false);
    }
  }, [
    formId,
    fileNoteId,
    rejectedReason,
    reloadFormContext,
    onClose,
    apiClient,
  ]);

  const handleComplete = useCallback(
    async (signatureData: string | undefined) => {
      if (!response.trim() || !signatoryName.trim()) return;

      if (!signatureData) {
        renderErrorToast({ message: "Please provide your signature" });
        return;
      }

      setIsSubmitting(true);
      try {
        const result = await apiClient.completeDeMinimis(formId, {
          fileNoteId,
          response: response.trim(),
          signature: signatureData,
          signatoryName: signatoryName.trim(),
        });
        if (result.status === 204) {
          renderSuccessToast({
            message: "De minimis correction completed successfully",
          });
          reloadFormContext();
          onClose();
        }
      } catch (e: unknown) {
        renderErrorToast({
          message: e instanceof Error ? e.message : "An error occurred",
        });
        setIsSubmitting(false);
      }
    },
    [
      formId,
      fileNoteId,
      response,
      signatoryName,
      reloadFormContext,
      onClose,
      apiClient,
    ],
  );

  // Review step: show request details + accept/reject
  if (step === "review" && (isPending || isAccepted)) {
    return (
      <PopupDialog open={true} onClose={onClose} maxWidth="md">
        <PopupDialogTitle
          titleText="De Minimis Correction Request"
          closeDialog={onClose}
        />
        <Box sx={{ mb: 2 }}>
          <Banner
            body={[
              `A de minimis (minor) correction has been requested for ${formDescription}.`,
              "Please review the details below and accept or reject this request.",
            ]}
            bannerType={BannerList.INFO}
          />
        </Box>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Error type
          </Typography>
          <Typography>{errorTypeLabel}</Typography>
        </Box>
        {resolvedFields.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Fields identified for correction
            </Typography>
            <Box
              sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                p: 1.5,
              }}
            >
              {resolvedFields.map((field) => (
                <Box
                  key={field.fieldKey}
                  sx={{ mb: 1, "&:last-child": { mb: 0 } }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                    }}
                  >
                    {field.label}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      fontSize: "0.8rem",
                    }}
                  >
                    {field.displayValue}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Details from the MHA admin
          </Typography>
          <Typography>{fileNote.note}</Typography>
        </Box>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Requested by
          </Typography>
          <Typography>{fileNote.createdBy}</Typography>
        </Box>
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            marginTop: theme.spacing(3),
          }}
        >
          <Button
            label="Reject"
            onClick={() => {
              setStep("reject");
            }}
            variant="outlined"
            color="error"
            disabled={isSubmitting}
          />
          <Button
            color="primary"
            disabled={isSubmitting}
            label="Make Correction"
            onClick={() => {
              setStep("complete-details");
            }}
          />
        </Box>
      </PopupDialog>
    );
  }

  // Reject step
  if (step === "reject") {
    return (
      <PopupDialog open={true} onClose={onClose} maxWidth="md">
        <PopupDialogTitle
          titleText="Reject De Minimis Correction"
          closeDialog={onClose}
        />
        <Box sx={{ mb: 2 }}>
          <Banner
            body={[
              "Please provide a reason for rejecting this correction request. The MHA admin will be notified.",
            ]}
            bannerType={BannerList.WARNING}
          />
        </Box>

        <TextField
          name="rejectedReason"
          label="Reason for rejection"
          onChange={(e: string) => {
            setRejectedReason(e);
          }}
          value={rejectedReason}
          useFullWidth={true}
          multiline
          rows="4"
        />

        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            marginTop: theme.spacing(3),
          }}
        >
          <Button
            label="Back"
            onClick={() => {
              setStep("review");
            }}
            variant="outlined"
            disabled={isSubmitting}
          />
          <Button
            color="error"
            disabled={isSubmitting || !rejectedReason.trim()}
            label="Confirm Rejection"
            onClick={() => {
              void handleReject();
            }}
          />
        </Box>
      </PopupDialog>
    );
  }

  // Complete details step
  if (step === "complete-details") {
    return (
      <PopupDialog open={true} onClose={onClose} maxWidth="md">
        <PopupDialogTitle
          titleText="Complete De Minimis Correction"
          closeDialog={onClose}
        />
        <Box sx={{ mb: 2 }}>
          <Banner
            body={[
              `Please describe the correction you have made to ${formDescription}.`,
            ]}
            bannerType={BannerList.INFO}
          />
        </Box>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Original request
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
            }}
          >
            {fileNote.deMinimisErrorType}: {fileNote.note}
          </Typography>
        </Box>
        <Box sx={{ mb: 2 }}>
          <TextField
            name="response"
            label="Describe the correction made"
            onChange={(e: string) => {
              setResponse(e);
            }}
            value={response}
            useFullWidth={true}
            multiline
            rows="4"
          />
        </Box>
        {!userName && (
          <Box sx={{ mb: 2 }}>
            <TextField
              name="signatoryName"
              label="Your full name"
              onChange={(e: string) => {
                setSignatoryName(e);
              }}
              value={signatoryName}
              useFullWidth={true}
            />
          </Box>
        )}
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            marginTop: theme.spacing(3),
          }}
        >
          <Button
            label="Back"
            onClick={() => {
              setStep("review");
            }}
            variant="outlined"
            disabled={isSubmitting}
          />
          <Button
            color="primary"
            disabled={!response.trim() || !signatoryName.trim()}
            label="Continue to Sign"
            onClick={() => {
              setStep("complete-sign");
            }}
          />
        </Box>
      </PopupDialog>
    );
  }

  // Complete sign step — uses the standard SignatureDialog
  if (step === "complete-sign") {
    return (
      <SignatureDialog
        onClose={() => {
          setStep("complete-details");
        }}
        onHappyWithSignature={async (signature) => {
          await handleComplete(signature);
        }}
        confirmButtonLabel="Confirm Correction"
        clearButtonLabel="Clear"
        userDefaultSignature={userDefaultSignature}
        allowUseOfDefaultSignature={!!userDefaultSignature}
        showSaveDefaultSignatureOption={false}
      />
    );
  }

  // Fallback
  return null;
}
